/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { classes, Slot } from '@adobe-commerce/elsie/lib';
import { useGetAttributesForm } from '@/auth/hooks/api/useGetAttributesForm';
import { useGetStoreConfigs } from '@/auth/hooks/api/useGetStoreConfigs';
import { usePasswordValidationMessage } from '@/auth/hooks/components/usePasswordValidationMessage';
import { useSignUpForm } from '@/auth/hooks/components/useSignUpForm';
import { SignUpFormProps } from '@/auth/types';
import { useInLineAlert } from '@/auth/hooks/useInLineAlert';
import { Form, Button, EmailConfirmationForm } from '@/auth/components';
import {
  Field,
  Checkbox,
  InLineAlert,
  InputPassword,
  Header,
} from '@adobe-commerce/elsie/components';
import SkeletonLoader from '../SkeletonLoader';
import '@/auth/components/SignUpForm/SignUpForm.css';
import { useMemo } from 'preact/hooks';
import { useCustomTranslations } from '@/auth/hooks/useCustomTranslations';

export const SignUpForm: FunctionComponent<SignUpFormProps> = ({
  requireRetypePassword = false,
  addressesData,
  formSize = 'default',
  inputsDefaultValueSet,
  fieldsConfigForApiVersion1,
  apiVersion2 = true,
  isAutoSignInEnabled = true,
  hideCloseBtnOnEmailConfirmation = false,
  routeRedirectOnEmailConfirmationClose,
  routeRedirectOnSignIn,
  routeSignIn,
  onErrorCallback,
  onSuccessCallback,
  setActiveComponent,
  slots,
}) => {
  /**
   * useCustomTranslations is required to support extensibility of error messages.
   * Ensure all error-related translation paths include ".default"
   * to allow future handling of dynamic or nested error messages.
   */
  const translations = useCustomTranslations({
    title: 'Auth.SignUpForm.title',
    buttonPrimary: 'Auth.SignUpForm.buttonPrimary',
    buttonSecondary: 'Auth.SignUpForm.buttonSecondary',
    keepMeLoggedText: 'Auth.SignUpForm.keepMeLoggedText',
    customerTokenErrorMessage: 'Auth.Api.customerTokenErrorMessage',
    failedCreateCustomerAddress: 'Auth.SignUpForm.failedCreateCustomerAddress',
    placeholder: 'Auth.InputPassword.placeholder',
    floatingLabel: 'Auth.InputPassword.floatingLabel',
    requiredFieldError: 'Auth.FormText.requiredFieldError.default',
    confirmPasswordPlaceholder: 'Auth.SignUpForm.confirmPassword.placeholder',
    confirmPasswordFloatingLabel:
      'Auth.SignUpForm.confirmPassword.floatingLabel',
    passwordMismatch: 'Auth.SignUpForm.confirmPassword.passwordMismatch', //NOSONAR
  });

  const { passwordConfigs, isEmailConfirmationRequired } = useGetStoreConfigs();
  const { fieldsListConfigs } = useGetAttributesForm({
    fieldsConfigForApiVersion1,
    apiVersion2,
    inputsDefaultValueSet,
  });

  const { inLineAlertProps, handleSetInLineAlertProps } = useInLineAlert();

  const {
    showPasswordErrorMessage,
    confirmPassword,
    confirmPasswordMessage,
    isKeepMeLogged,
    userEmail,
    showEmailConfirmationForm,
    isSuccessful,
    isClickSubmit,
    signUpPasswordValue,
    isLoading,
    onSubmitSignUp,
    signInButton,
    handleSetSignUpPasswordValue,
    onKeepMeLoggedChange,
    handleHideEmailConfirmationForm,
    handleConfirmPasswordChange,
    onBlurPassword,
    onBlurConfirmPassword,
  } = useSignUpForm({
    requireRetypePassword,
    addressesData,
    translations,
    isEmailConfirmationRequired,
    apiVersion2,
    passwordConfigs,
    isAutoSignInEnabled,
    routeRedirectOnSignIn,
    routeSignIn,
    onErrorCallback,
    onSuccessCallback,
    setActiveComponent,
    handleSetInLineAlertProps,
    routeRedirectOnEmailConfirmationClose,
  });

  const { isValidUniqueSymbols, defaultLengthMessage } =
    usePasswordValidationMessage({
      password: signUpPasswordValue,
      isClickSubmit,
      passwordConfigs,
    });

  const validationPasswordMessage = useMemo(() => {
    if (showPasswordErrorMessage) {
      return translations.requiredFieldError;
    }

    const hasSubmitError =
      isValidUniqueSymbols === 'error' ||
      defaultLengthMessage?.status === 'error';

    if (hasSubmitError) {
      return ' ';
    }

    return '';
  }, [
    defaultLengthMessage?.status,
    isValidUniqueSymbols,
    showPasswordErrorMessage,
    translations.requiredFieldError,
  ]);

  const shouldShowPersistLoginCheckbox =
    !isEmailConfirmationRequired && addressesData?.length;

  if (!fieldsListConfigs.length && apiVersion2) {
    return (
      <div
        className={`auth-sign-up-form auth-sign-up-form--${formSize} skeleton-loader`}
        data-testid="SignUpForm"
      >
        <SkeletonLoader activeSkeleton="signUpForm" />
      </div>
    );
  }

  if (isSuccessful.status && slots?.SuccessNotification) {
    return (
      <Slot
        data-testid="successNotificationTestId"
        name="SuccessNotification"
        slot={slots?.SuccessNotification}
        context={{ isSuccessful }}
      />
    );
  }

  if (showEmailConfirmationForm) {
    return (
      <EmailConfirmationForm
        formSize={formSize}
        userEmail={userEmail}
        inLineAlertProps={inLineAlertProps}
        hideCloseBtnOnEmailConfirmation={hideCloseBtnOnEmailConfirmation}
        handleSetInLineAlertProps={handleSetInLineAlertProps}
        onPrimaryButtonClick={handleHideEmailConfirmationForm}
      />
    );
  }

  const slotContext = {
    userEmail,
    signUpPasswordValue,
    confirmPassword,
    showPasswordErrorMessage,
    confirmPasswordMessage,
    isLoading,
    isSuccessful,
  };

  return (
    <div
      className={classes([
        'auth-sign-up-form',
        `auth-sign-up-form--${formSize}`,
      ])}
      data-testid="SignUpForm"
    >
      <Slot
        name="Title"
        data-testid="title"
        slot={slots?.Title}
        context={{ ...slotContext }}
      >
        <Header
          title={translations.title}
          divider={false}
          className="auth-sign-up-form__title"
        />
      </Slot>
      {inLineAlertProps.text ? (
        <InLineAlert
          className="auth-sign-up-form__notification"
          type={inLineAlertProps.type}
          variant="secondary"
          heading={inLineAlertProps.text}
          icon={inLineAlertProps.icon}
        />
      ) : null}
      <Form
        onSubmit={onSubmitSignUp}
        className="auth-sign-up-form__form"
        loading={isLoading}
        name="signUp_form"
        fieldsConfig={fieldsListConfigs}
        slots={slots}
        formSlot={{
          name: 'Form',
          slot: slots?.Form,
          context: slotContext,
          dataTestId: 'form',
        }}
      >
        <InputPassword
          validateLengthConfig={defaultLengthMessage}
          className="auth-sign-up-form__form__field"
          autoComplete={'current-password'}
          name={'password'}
          minLength={passwordConfigs?.minLength}
          errorMessage={validationPasswordMessage}
          defaultValue={signUpPasswordValue}
          uniqueSymbolsStatus={isValidUniqueSymbols}
          requiredCharacterClasses={passwordConfigs?.requiredCharacterClasses}
          onValue={handleSetSignUpPasswordValue}
          placeholder={translations.placeholder}
          floatingLabel={translations.floatingLabel}
          onBlur={onBlurPassword}
        >
          {requireRetypePassword ? (
            <div className="auth-sign-up-form__form__confirm-wrapper">
              <InputPassword
                className="auth-sign-up-form__form__field auth-sign-up-form__form__field--confirm-password"
                autoComplete="confirmPassword"
                name="confirmPasswordField"
                placeholder={translations.confirmPasswordPlaceholder}
                floatingLabel={translations.confirmPasswordFloatingLabel}
                errorMessage={confirmPasswordMessage}
                defaultValue={confirmPassword}
                onValue={handleConfirmPasswordChange}
                onBlur={onBlurConfirmPassword}
              />
            </div>
          ) : null}

          {shouldShowPersistLoginCheckbox ? (
            <div
              className={'auth-sign-up-form__automatic-login'}
              data-testid="automaticLogin"
            >
              <Field>
                <Checkbox
                  name=""
                  placeholder={translations.keepMeLoggedText}
                  label={translations.keepMeLoggedText}
                  checked={isKeepMeLogged}
                  onChange={onKeepMeLoggedChange}
                />
              </Field>
            </div>
          ) : null}
        </InputPassword>

        <Slot
          name="PrivacyPolicyConsent"
          data-testid={'privacyPolicyConsent'}
          slot={slots?.PrivacyPolicyConsent}
          key={'privacyPolicyConsent'}
        />

        <Slot
          name="Buttons"
          className="auth-sign-up-form-buttons"
          data-testid="buttons"
          slot={slots?.Buttons}
          context={{ ...slotContext }}
        >
          <Button
            className="auth-sign-up-form-buttons--signin"
            type="button"
            variant="tertiary"
            style={{ padding: 0 }}
            buttonText={translations.buttonSecondary}
            enableLoader={false}
            onClick={signInButton}
          />
          <Button
            type="submit"
            buttonText={translations.buttonPrimary}
            variant="primary"
            enableLoader={isLoading}
          />
        </Slot>
      </Form>
      <div id="createCustomerV2" />
    </div>
  );
};
